import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ChatPage from './pages/ChatPage';
import LandingPage from './pages/LandingPage';

import AdminDashboard from './pages/AdminDashboard';
import ChatLogsPage from './pages/ChatLogsPage';
import EmailStatsPage from './pages/EmailStatsPage';
import InstructAIPage from './pages/InstructAIPage';
import WidgetAnalyticsPage from './pages/WidgetAnalyticsPage';
import WidgetChatsPage from './pages/WidgetChatsPage';
import WidgetManagerPage from './pages/WidgetManagerPage';
import UrlTrainerPage from './pages/UrlTrainerPage';
import AdminBrandRouterPage from './pages/AdminBrandRouterPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminHandoverPage from './pages/AdminHandoverPage';
import AdminHandoverThreadPage from './pages/AdminHandoverThreadPage';
import AdminAgentAnalyticsPage from './pages/AdminAgentAnalyticsPage';
import AgentActivityDetailPage from './pages/AgentActivityDetailPage';
import NotificationsPage from './pages/NotificationsPage';

import AdminBootstrapPage from './pages/AdminBootstrapPage';
import AdminRegisterPage from './pages/AdminRegisterPage';

import LoginPage from './pages/LoginPage';
import CmsPage from './pages/CmsPage';
import SignupPage from './pages/SignupPage';
import PasswordResetRequestPage from './pages/PasswordResetRequestPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import PricingPage from './pages/PricingPage';
import AdminLandingPage from './pages/AdminLandingPage';

import AdminGate from './routes/AdminGate';
import AdminRoute from './routes/AdminRoute';

import UserChannelsPage from './pages/user/UserChannelsPage';
import UserTrainingPage from './pages/user/UserTrainingPage';
import UserWidgetsPage from './pages/user/UserWidgetsPage';
import UserOverviewPage from './pages/user/UserOverviewPage';
import UserChatHistoryPage from './pages/user/UserChatHistoryPage';
import UserAnalyticsPage from './pages/user/UserAnalyticsPage';
import UserWidgetAnalyticsPage from './pages/user/UserWidgetAnalyticsPage';
import UserWidgetChatsPage from './pages/user/UserWidgetChatsPage';
import UserRoute from './routes/UserRoute';
import UserAgentInboxPage from './pages/user/UserAgentInboxPage';
import UserAgentAnalyticsPage from './pages/user/UserAgentAnalyticsPage';
import UserSettingsPage from './pages/user/UserSettingsPage';
import UserNotificationsPage from './pages/user/UserNotificationsPage';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/landing.css';

export default function App() {
  const token = useSelector((s) => s.auth.token);
  const isAdmin = useSelector((s) => s.auth.is_admin || s.auth.is_staff || s.auth.is_superuser);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/pages/:slug" element={<CmsPage />} />

        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<PasswordResetRequestPage />} />
        <Route path="/reset-password/confirm" element={<PasswordResetConfirmPage />} />

        <Route path="/admin/bootstrap" element={<AdminBootstrapPage />} />

        <Route
          path="/admin/login"
          element={
            <AdminGate>
              {token && isAdmin ? <Navigate to="/admin" replace /> : <LoginPage />}
            </AdminGate>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/register"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminRegisterPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/chatlogs"
          element={
            <AdminGate>
              <AdminRoute>
                <ChatLogsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/stats"
          element={
            <AdminGate>
              <AdminRoute>
                <EmailStatsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/instruct"
          element={
            <AdminGate>
              <AdminRoute>
                <InstructAIPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/branding"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminBrandRouterPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/widgets/:token/analytics"
          element={
            <AdminGate>
              <AdminRoute>
                <WidgetAnalyticsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/widgets/:token/chats"
          element={
            <AdminGate>
              <AdminRoute>
                <WidgetChatsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/widgets"
          element={
            <AdminGate>
              <AdminRoute>
                <WidgetManagerPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/settings"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminSettingsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/landing"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminLandingPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/agent-inbox"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminHandoverPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/agent-analytics"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminAgentAnalyticsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/handover/:handoverId"
          element={
            <AdminGate>
              <AdminRoute>
                <AdminHandoverThreadPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/agent-activity/:agentId"
          element={
            <AdminGate>
              <AdminRoute>
                <AgentActivityDetailPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/notifications"
          element={
            <AdminGate>
              <AdminRoute>
                <NotificationsPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/train-url"
          element={
            <AdminGate>
              <AdminRoute>
                <UrlTrainerPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route
          path="/admin/training"
          element={
            <AdminGate>
              <AdminRoute>
                <UrlTrainerPage />
              </AdminRoute>
            </AdminGate>
          }
        />

        <Route path="/dashboard" element={<UserRoute><UserOverviewPage /></UserRoute>} />
        <Route path="/dashboard/subscription" element={<UserRoute><UserOverviewPage /></UserRoute>} />
        <Route path="/dashboard/widgets" element={<UserRoute><UserWidgetsPage /></UserRoute>} />
        <Route path="/dashboard/widgets/:token/analytics" element={<UserRoute><UserWidgetAnalyticsPage /></UserRoute>} />
        <Route path="/dashboard/widgets/:token/chats" element={<UserRoute><UserWidgetChatsPage /></UserRoute>} />
        <Route path="/dashboard/training" element={<UserRoute><UserTrainingPage /></UserRoute>} />
        <Route path="/dashboard/channels" element={<UserRoute><UserChannelsPage /></UserRoute>} />
        <Route path="/dashboard/history" element={<UserRoute><UserChatHistoryPage /></UserRoute>} />
        <Route path="/dashboard/agent-inbox" element={<UserRoute><UserAgentInboxPage /></UserRoute>} />
        <Route path="/dashboard/agent-analytics" element={<UserRoute><UserAgentAnalyticsPage /></UserRoute>} />
        <Route path="/dashboard/agent-activity/:agentId" element={<UserRoute><AgentActivityDetailPage /></UserRoute>} />
        <Route path="/dashboard/settings" element={<UserRoute><UserSettingsPage /></UserRoute>} />
        <Route path="/dashboard/analytics" element={<UserRoute><UserAnalyticsPage /></UserRoute>} />
        <Route path="/dashboard/notifications" element={<UserRoute><UserNotificationsPage /></UserRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}